import ACTION_REGISTRY from './action_bridge_registry';

export const BRIDGE_VERSION = 1;

/**
 * Installs `window.gladly.agentDesktop`, a narrow entry point for running a
 * vetted subset of actions from outside the React tree.
 *
 * The app already exposes stores, gateways and the router on `window` for
 * console debugging. Those cover reads and navigation, but not writes: an
 * action is far more than its gateway call — `GoAway`, for instance, also stops
 * the capacity manager, cancels agent polling, logs activity, records analytics
 * and reconciles the store optimistically. Calling the gateway directly leaves
 * the agent Away on the server while still receiving routed work. So writes
 * have to go through `executeAction`, and this is the supported way to reach it.
 *
 * The registry is an explicit allowlist rather than a lookup by module path,
 * which keeps the exposed surface reviewable in one file and keeps every action
 * statically imported.
 *
 * Gated on the `actionBridge` feature flag, resolved per access rather than
 * once here. This runs during bootstrap, long before the feature set arrives
 * post-auth from FeatureSetGatewayObserver, and `AppFeatures.isEnabled` answers
 * false until all three sources have loaded — so an eager check would be false
 * every time and the bridge would never appear. The getter also means flipping
 * the flag takes effect on the next access, with no reload.
 *
 * @param {ExtendedRunnerContext} runnerContext - must be the extended context
 *   the app gives its actions, so route-scoped stores (the current customer's
 *   nested stores) resolve correctly.
 */
export default function configureActionBridge(runnerContext) {
  const bridge = {
    version: BRIDGE_VERSION,

    listActions: () => Object.keys(ACTION_REGISTRY).sort(),

    executeAction: (name, params) => {
      const entry = ACTION_REGISTRY[name];
      if (!entry) {
        throw new Error(`Unknown action: ${name}`);
      }

      const args = entry.adapt ? entry.adapt(params || {}) : params;
      return runnerContext.executeAction(entry.action, args);
    },
  };

  window.gladly = window.gladly || {};
  Object.defineProperty(window.gladly, 'agentDesktop', {
    configurable: true,
    enumerable: true,
    get: () => (isBridgeEnabled(runnerContext) ? bridge : undefined),
  });

  return bridge;
}

function isBridgeEnabled(runnerContext) {
  try {
    return runnerContext.stores.appFeatures.get().isEnabled('actionBridge');
  } catch (e) {
    return false;
  }
}
